import React, { useState, useRef } from 'react';
import { useAPR } from '../context/APRContext';
import { Application } from '../types';
import apiService, { authFetch } from '../services/api';

const ApplicationMasterData: React.FC = () => {
  const { state, dispatch, refreshData } = useAPR();
  const [showForm, setShowForm] = useState(false);
  const [editingApplication, setEditingApplication] = useState<Application | null>(null);
  const [uploadedData, setUploadedData] = useState<any[]>([]);
  const [showPreview, setShowPreview] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [filterText, setFilterText] = useState('');
  const [filterVendor, setFilterVendor] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const updateFileInputRef = useRef<HTMLInputElement>(null);
  const [uploadedUpdateData, setUploadedUpdateData] = useState<any[]>([]);
  const [showUpdatePreview, setShowUpdatePreview] = useState(false);
  const [uploadMode, setUploadMode] = useState<'create' | 'update'>('create');
  const [viewingApplication, setViewingApplication] = useState<Application | null>(null);

  // Format numbers with commas (e.g., 1010 -> 1,010)
  const formatNumber = (num: number): string => {
    return num.toLocaleString('en-US');
  };

  // Convert various date formats to YYYY-MM-DD for HTML date input
  const formatDateForInput = (dateStr: string): string => {
    if (!dateStr) return '';
    
    // Try different date formats
    let date: Date | null = null;
    
    // Format: M/D/YY (like "8/31/16" or "3/10/97")
    if (dateStr.match(/^\d{1,2}\/\d{1,2}\/\d{2}$/)) {
      const parts = dateStr.split('/');
      const twoDigitYear = parseInt(parts[2]);
      // Use cutoff: 00-49 = 2000-2049, 50-99 = 1950-1999
      const year = twoDigitYear >= 50 ? 1900 + twoDigitYear : 2000 + twoDigitYear;
      date = new Date(year, parseInt(parts[0]) - 1, parseInt(parts[1]));
    }
    // Format: M/D/YYYY (like "8/31/2016")
    else if (dateStr.match(/^\d{1,2}\/\d{1,2}\/\d{4}$/)) {
      const parts = dateStr.split('/');
      date = new Date(parseInt(parts[2]), parseInt(parts[0]) - 1, parseInt(parts[1]));
    }
    // Format: YYYY-MM-DD (already correct)
    else if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return dateStr;
    }
    // Try parsing as-is
    else {
      date = new Date(dateStr);
    }
    
    if (date && !isNaN(date.getTime())) {
      return date.toISOString().split('T')[0]; // Convert to YYYY-MM-DD
    }
    
    return ''; // Return empty if parsing failed
  };
  
  const [formData, setFormData] = useState({
    name: '',
    applicationIdCode: '',
    itHardwareInfrastructure: '',
    dateOfIntroduction: '',
    applicationOwner: '',
    appOwnerEmail: '',
    technicalOwnerCustodian: '',
    toEmail: '',
    businessFunctionDomain: '',
    applicationType: '',
    technologyStack: '',
    currentOperating: '',
    userCount: 0,
    businessCriticality: '',
    technicalHealthStatus: '',
    integrationDependencies: '',
    vendorSupplier: '',
    parentApplicationId: '',
    applicationDescription: '',
    internalNumber: '',
    l1BusinessFunctionalArea: '',
    l2CapabilityFunction: '',
    customerId: '',
    aprModelId: state.models[0]?.id || '',
    currentRunRate: 0,
    plannedCost: 0,
    migrationCost: 0,
    futureRunRate: 0
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: name === 'userCount' ? parseInt(value) || 0 : value 
    }));
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      console.log('No file selected');
      return;
    }

    console.log('File selected:', file.name, 'Type:', file.type, 'Size:', file.size);

    const maxSize = 50 * 1024 * 1024; // 50MB
    if (file.size > maxSize) {
      alert('File size exceeds 50MB limit. Please use a smaller file.');
      return;
    }

    if (!file.name.toLowerCase().endsWith('.csv')) {
      alert('Please upload a CSV file. Excel files (.xlsx, .xls) are not yet supported.');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    console.log('Starting to read CSV file...');
    const reader = new FileReader();
    reader.onload = (event) => {
      console.log('File read complete, processing...');
      try {
        let text = event.target?.result as string;
        
        // Handle potential encoding issues
        if (text.includes('�') || text.includes('\uFFFD')) {
          console.warn('Detected encoding issues, attempting to re-read with different encoding');
          // Try to read again with different encoding assumption
          text = text.replace(/[^\x00-\x7F]/g, ''); // Remove non-ASCII characters as fallback
        }
        
        // Handle BOM (Byte Order Mark) if present
        if (text.charCodeAt(0) === 0xFEFF) {
          text = text.slice(1);
        }
        
        // Normalize line endings
        text = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');

        // Enhanced CSV parsing that handles quotes, commas, and various delimiters
        const parseCSVLine = (line: string): string[] => {
          const result: string[] = [];
          let current = '';
          let inQuotes = false;

          for (let i = 0; i < line.length; i++) {
            const char = line[i];

            if (char === '"') {
              inQuotes = !inQuotes;
            } else if (char === ',' && !inQuotes) {
              result.push(current.trim());
              current = '';
            } else if (char !== '\r') {
              current += char;
            }
          }

          result.push(current.trim());
          return result;
        };

        // Parse CSV respecting multiline quoted fields
        const parseCSVRows = (csvText: string): string[] => {
          const rows: string[] = [];
          let current = '';
          let inQuotes = false;

          for (let i = 0; i < csvText.length; i++) {
            const char = csvText[i];
            if (char === '"') {
              inQuotes = !inQuotes;
              current += char;
            } else if (char === '\n' && !inQuotes) {
              if (current.trim()) {
                rows.push(current);
              }
              current = '';
            } else {
              current += char;
            }
          }
          if (current.trim()) {
            rows.push(current);
          }
          return rows;
        };

        const lines = parseCSVRows(text);
        console.log('Total lines found:', lines.length);
        console.log('First few lines:', lines.slice(0, 3));
        
        if (lines.length < 2) {
          alert('File appears to be empty or contains only headers. Please check your file format.');
          return;
        }

        const headers = parseCSVLine(lines[0]).map(h => 
          h.replace(/['"]/g, '').trim().replace(/[^\w\s\/\-]/g, '')
        );
        console.log('Parsed headers:', headers);
        console.log('Header count:', headers.length);
        
        if (headers.length === 0) {
          alert('No headers detected. Please check your file format.');
          return;
        }
        
        const data = lines.slice(1).map((line, index) => {
          const values = parseCSVLine(line).map(v => 
            v.replace(/['"]/g, '').trim()
          );
          const obj: any = {};
          
          headers.forEach((header, headerIndex) => {
            const value = values[headerIndex] || '';
            obj[header] = value;
          });
          
          console.log(`Row ${index + 1}:`, obj);
          return obj;
        }).filter(row => {
          // Filter out completely empty rows
          const hasData = Object.values(row).some(value => 
            value && value.toString().trim() !== '' && value.toString().trim() !== 'N/A'
          );
          return hasData;
        });

        console.log('Processed data count:', data.length);
        console.log('Sample processed data:', data.slice(0, 2));

        if (data.length === 0) {
          alert('No valid data rows found in the file. Please check that your file contains data rows with values.');
          return;
        }

        console.log('Setting uploadedData with', data.length, 'records');
        setUploadedData(data);
        console.log('Setting showPreview to true');
        setShowPreview(true);
        console.log('Preview should now be visible');
      } catch (error) {
        console.error('Error parsing file:', error);
        alert(`Error parsing file: ${error instanceof Error ? error.message : 'Unknown error'}. Please check the file format and try again.`);
      }
    };
    
    // Try UTF-8 encoding first
    reader.readAsText(file, 'UTF-8');
  };

  const processUploadedData = async () => {
    console.log('Processing uploaded data:', uploadedData);
    
    try {
      // No dependencies required for application upload

      // Prepare application data for bulk import
      const applications = uploadedData.map((row, index) => {
        // More flexible header matching - try different variations
        const getFieldValue = (possibleHeaders: string[]): string => {
          for (const header of possibleHeaders) {
            if (row[header] !== undefined && row[header] !== null) {
              return String(row[header]).trim();
            }
          }
          return '';
        };

        // Get parent application value and resolve to ID if it's an application code
        const parentAppValue = getFieldValue(['Parent Application ID', 'Parent Application', 'Parent App ID', 'ParentApplicationId', 'Parent App']);
        // Filter out header-like values that might have been accidentally captured
        const cleanParentValue = parentAppValue &&
          !parentAppValue.toLowerCase().includes('parent application') &&
          parentAppValue.trim() !== '' ? parentAppValue : null;

        return {
          name: getFieldValue(['Application Name', 'ApplicationName', 'App Name', 'name']) || `Application_${index + 1}`,
          description: getFieldValue(['Description', 'App Description']),
          application_id_code: getFieldValue(['Application ID/Code', 'Application ID', 'App ID', 'ID', 'ApplicationID', 'Application IDCode']),
          it_hardware_infrastructure: getFieldValue(['IT Hardware/Infrastructure', 'IT HardwareInfrastructure', 'IT Hardware Infrastructure', 'Infrastructure', 'Hardware', 'ITHardwareInfrastructure']),
          date_of_introduction: getFieldValue(['Date of Introduction', 'Desktop Application Date of Introduction', 'Introduction Date', 'Date', 'DateOfIntroduction', 'Date Introduced']),
          application_owner: getFieldValue(['Application Owner', 'Owner', 'App Owner', 'ApplicationOwner']),
          app_owner_email: getFieldValue(['App Owner Email', 'Application Owner Email', 'Application Owner email', 'App Owner email', 'Owner Email', 'AppOwnerEmail', 'OwnerEmail']),
          technical_owner_custodian: getFieldValue(['Technical Owner/Custodian', 'Technical OwnerCustodian', 'Technical Owner', 'Custodian', 'TechnicalOwner', 'Tech Owner']),
          to_email: getFieldValue(['Technical Owner Email', 'TO Email', 'Technical Email', 'Tech Email', 'TechnicalOwnerEmail', 'TOEmail']),
          business_function_domain: getFieldValue(['Business Function/Domain', 'Business FunctionDomain', 'Business Function', 'Domain', 'BusinessFunction', 'Business Domain']),
          application_type: getFieldValue(['Application Type', 'Type', 'App Type', 'ApplicationType']),
          technology_stack: getFieldValue(['Technology Stack', 'Technology', 'Tech Stack', 'TechnologyStack']),
          current_operating: getFieldValue(['Current Operating', 'current_operating', 'CurrentOperating']),
          user_count: parseInt(getFieldValue(['User Count', 'Users', 'UserCount', 'User']) || '0') || 0,
          business_criticality: getFieldValue(['Business Criticality', 'Criticality', 'BusinessCriticality']),
          technical_health_status: getFieldValue(['Technical Health Status', 'Health Status', 'Technical Health', 'TechnicalHealthStatus', 'TechnicalHealth']),
          integration_dependencies: getFieldValue(['Integration Dependencies', 'Dependencies', 'Integrations', 'IntegrationDependencies']),
          vendor_supplier: getFieldValue(['Vendor/Supplier', 'VendorSupplier', 'Vendor', 'Supplier']),
          parent_application_id: cleanParentValue,
          application_description: getFieldValue(['Application Description', 'ApplicationDescription', 'App Description', 'Description']),
          internal_number: getFieldValue(['Internal Number', 'InternalNumber', 'Internal Ref', 'Ref Number']),
          l1_business_functional_area: getFieldValue(['L1 - Business Functional Area', 'L1 Business Functional Area', 'L1', 'Business Functional Area']),
          l2_capability_function: getFieldValue(['L2 - Capability Function', 'L2 Capability Function', 'L2', 'Capability Function'])
        };
      });

      // Use bulk import API
      const result = await apiService.bulkImportApplications(applications);

      setUploadedData([]);
      setShowPreview(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      // Show detailed results
      const { imported, skipped, errors } = result;
      if (errors.length === 0) {
        alert(`✅ Import successful! ${imported} applications imported.`);
      } else if (imported > 0) {
        const errorSummary = errors.slice(0, 10).join('\n'); // Show first 10 errors
        const moreErrors = errors.length > 10 ? `\n... and ${errors.length - 10} more errors` : '';
        alert(`⚠️ Import completed with issues:\n• ${imported} applications imported\n• ${skipped} applications skipped (duplicates)\n\nFirst errors:\n${errorSummary}${moreErrors}`);
      } else {
        const errorSummary = errors.slice(0, 10).join('\n');
        const moreErrors = errors.length > 10 ? `\n... and ${errors.length - 10} more errors` : '';
        alert(`❌ Import failed:\n• ${skipped} applications skipped (duplicates)\n• No applications imported\n\nErrors:\n${errorSummary}${moreErrors}`);
      }

      // Refresh the applications list if any were imported
      if (imported > 0) {
        await refreshData();
      }
    } catch (error: any) {
      alert(`Error during import: ${error.message || 'Unknown error'}`);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const applicationData = {
        name: formData.name,
        description: '',
        application_id_code: formData.applicationIdCode,
        it_hardware_infrastructure: formData.itHardwareInfrastructure,
        date_of_introduction: formData.dateOfIntroduction,
        application_owner: formData.applicationOwner,
        app_owner_email: formData.appOwnerEmail,
        technical_owner_custodian: formData.technicalOwnerCustodian,
        to_email: formData.toEmail,
        business_function_domain: formData.businessFunctionDomain,
        application_type: formData.applicationType,
        technology_stack: formData.technologyStack,
        current_operating: formData.currentOperating,
        user_count: formData.userCount,
        business_criticality: formData.businessCriticality,
        technical_health_status: formData.technicalHealthStatus,
        integration_dependencies: formData.integrationDependencies,
        vendor_supplier: formData.vendorSupplier,
        parent_application_id: formData.parentApplicationId || null,
        application_description: formData.applicationDescription || null,
        internal_number: formData.internalNumber || null,
        l1_business_functional_area: formData.l1BusinessFunctionalArea || null,
        l2_capability_function: formData.l2CapabilityFunction || null
      };

      if (editingApplication) {
        await apiService.updateApplication(editingApplication.id, applicationData);
        alert('Application updated successfully!');
      } else {
        await apiService.createApplication(applicationData);
        alert('Application created successfully!');
      }
      
      resetForm();
      // Refresh the data to show updated applications
      await refreshData();
    } catch (error: any) {
      alert(`Error ${editingApplication ? 'updating' : 'creating'} application: ${error.message || 'Unknown error'}`);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      applicationIdCode: '',
      itHardwareInfrastructure: '',
      dateOfIntroduction: '',
      applicationOwner: '',
      appOwnerEmail: '',
      technicalOwnerCustodian: '',
      toEmail: '',
      businessFunctionDomain: '',
      applicationType: '',
      technologyStack: '',
      currentOperating: '',
      userCount: 0,
      businessCriticality: '',
      technicalHealthStatus: '',
      integrationDependencies: '',
      vendorSupplier: '',
      parentApplicationId: '',
      applicationDescription: '',
      internalNumber: '',
      l1BusinessFunctionalArea: '',
      l2CapabilityFunction: '',
      customerId: '',
      aprModelId: '',
      currentRunRate: 0,
      plannedCost: 0,
      migrationCost: 0,
      futureRunRate: 0
    });
    setShowForm(false);
    setEditingApplication(null);
  };

  const handleView = (application: Application) => {
    setViewingApplication(application);
  };

  const handleEdit = (application: Application) => {
    setEditingApplication(application);
    setFormData({
      name: application.name,
      applicationIdCode: application.applicationIdCode || '',
      itHardwareInfrastructure: application.itHardwareInfrastructure || '',
      dateOfIntroduction: formatDateForInput(application.dateOfIntroduction || ''),
      applicationOwner: application.applicationOwner || '',
      appOwnerEmail: application.appOwnerEmail || '',
      technicalOwnerCustodian: application.technicalOwnerCustodian || '',
      toEmail: application.toEmail || '',
      businessFunctionDomain: application.businessFunctionDomain || '',
      applicationType: application.applicationType || '',
      technologyStack: application.technologyStack || '',
      currentOperating: application.currentOperating || '',
      userCount: application.userCount || 0,
      businessCriticality: application.businessCriticality || '',
      technicalHealthStatus: application.technicalHealthStatus || '',
      integrationDependencies: application.integrationDependencies || '',
      vendorSupplier: application.vendorSupplier || '',
      parentApplicationId: application.parentApplicationId || '',
      applicationDescription: application.applicationDescription || '',
      internalNumber: application.internalNumber || '',
      l1BusinessFunctionalArea: application.l1BusinessFunctionalArea || '',
      l2CapabilityFunction: application.l2CapabilityFunction || '',
      customerId: '',
      aprModelId: '',
      currentRunRate: application.costs.find(c => c.type === 'current_run_rate')?.amount || 0,
      plannedCost: application.costs.find(c => c.type === 'planned_cost')?.amount || 0,
      migrationCost: application.costs.find(c => c.type === 'migration_conversion')?.amount || 0,
      futureRunRate: application.costs.find(c => c.type === 'future_run_rate')?.amount || 0
    });
    setShowForm(true);
  };

  const handleDelete = async (applicationId: string) => {
    const application = state.applications.find(app => app.id === applicationId);
    const appName = application?.name || 'this application';
    
    if (window.confirm(`Are you sure you want to delete ${appName}?`)) {
      try {
        await apiService.deleteApplication(applicationId);
        alert('Application deleted successfully!');
        // Refresh the data to show updated applications
        await refreshData();
      } catch (error: any) {
        // Handle dependency error with detailed information
        if (error.response?.status === 400) {
          const errorData = error.response.data;
          if (errorData.details) {
            let message = `❌ Cannot Delete Application\n\n${errorData.error}\n\n`;
            
            if (errorData.details.reason === 'Application is part of an APR model') {
              message += `To delete this application:\n1. Remove it from the APR model '${errorData.details.modelName}'\n2. Then try deleting again`;
            } else if (errorData.details.reason === 'Application has assessment data') {
              message += `This application has ${errorData.details.responseCount} assessment response(s).\n\nTo delete this application:\n1. Remove all assessment data first\n2. Then try deleting again`;
            } else if (errorData.details.reason === 'Application is part of an APR run') {
              message += `To delete this application:\n1. Remove it from the APR run '${errorData.details.aprRunName}'\n2. Then try deleting again`;
            } else if (errorData.details.reason === 'Application has current run rate cost data') {
              message += `This application has ${errorData.details.costRecordCount} cost record(s).\n\nTo delete this application:\n1. Go to Current Run Rate menu\n2. Delete the cost record for this application\n3. Then return and try deleting the application again\n\n${errorData.details.suggestion || ''}`;
            }
            
            alert(message);
          } else {
            alert(`❌ Cannot Delete Application\n\n${errorData.error}`);
          }
        } else {
          alert(`Error deleting application: ${error.response?.data?.error || error.message || 'Unknown error'}`);
        }
      }
    }
  };

  const handleDeleteAllData = () => {
    setShowDeleteModal(true);
    setDeleteConfirmText('');
  };

  const handleConfirmDeleteAll = async () => {
    if (deleteConfirmText !== 'DELETE') {
      alert('Please type "DELETE" to confirm deletion');
      return;
    }

    try {
      let deletedCount = 0;
      let errorCount = 0;
      const errors: string[] = [];

      // Delete children first, then standalone, then parents — so parent delete check passes
      const deleteOrder = [
        ...state.applications.filter(a => a.parentApplicationId && !a.isParent),
        ...state.applications.filter(a => !a.isParent && !a.parentApplicationId),
        ...state.applications.filter(a => a.isParent)
      ];
      for (const app of deleteOrder) {
        try {
          await apiService.deleteApplication(app.id);
          deletedCount++;
        } catch (error: any) {
          errorCount++;
          // Handle detailed dependency errors
          if (error.response?.status === 400 && error.response.data?.details) {
            const details = error.response.data.details;
            if (details.reason === 'Application is part of an APR model') {
              errors.push(`${app.name}: Part of APR model '${details.modelName}'`);
            } else if (details.reason === 'Application has assessment data') {
              errors.push(`${app.name}: Has ${details.responseCount} assessment response(s)`);
            } else if (details.reason === 'Application is part of an APR run') {
              errors.push(`${app.name}: Part of APR run '${details.aprRunName}'`);
            } else {
              errors.push(`${app.name}: ${error.response.data.error || 'Unknown error'}`);
            }
          } else {
            errors.push(`${app.name}: ${error.response?.data?.error || error.message || 'Unknown error'}`);
          }
        }
      }
      
      setShowDeleteModal(false);
      setDeleteConfirmText('');
      
      if (errorCount === 0) {
        alert(`Successfully deleted all ${deletedCount} applications from the database`);
      } else {
        const message = `Delete completed: ${deletedCount} successful, ${errorCount} failed.\n\nErrors:\n${errors.join('\n')}`;
        alert(message);
      }
      
      // Refresh the data to show updated applications
      if (deletedCount > 0) {
        await refreshData();
      }
    } catch (error: any) {
      alert(`Error during bulk delete: ${error.message || 'Unknown error'}`);
    }
  };

  const handleCancelDeleteAll = () => {
    setShowDeleteModal(false);
    setDeleteConfirmText('');
  };

  const handleUpdateFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }

    if (!file.name.toLowerCase().endsWith('.csv')) {
      alert('Please upload a CSV file. Excel files (.xlsx, .xls) are not yet supported.');
      if (updateFileInputRef.current) {
        updateFileInputRef.current.value = '';
      }
      return;
    }

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        let text = event.target?.result as string;
        text = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
        
        const parseCSVLine = (line: string): string[] => {
          const result: string[] = [];
          let current = '';
          let inQuotes = false;
          
          for (let i = 0; i < line.length; i++) {
            const char = line[i];
            if (char === '"' && (i === 0 || line[i-1] === ',')) {
              inQuotes = !inQuotes;
            } else if (char === ',' && !inQuotes) {
              result.push(current);
              current = '';
            } else {
              current += char;
            }
          }
          result.push(current);
          return result;
        };

        const lines = text.split('\n').filter(line => line.trim());
        if (lines.length < 2) {
          alert('File appears to be empty or contains only headers. Please check your file format.');
          return;
        }

        const headers = parseCSVLine(lines[0]).map(h => 
          h.replace(/['"]/g, '').trim().replace(/[^\w\s\/\-]/g, '')
        );
        
        // Check if applicationIdCode is present (required for updates)
        const hasIdColumn = headers.some(h => 
          h.toLowerCase().includes('application') && (h.toLowerCase().includes('id') || h.toLowerCase().includes('code'))
        );
        
        if (!hasIdColumn) {
          alert('Update file must contain an Application ID or Application Code column to identify which applications to update.');
          return;
        }

        const data = lines.slice(1).map((line, index) => {
          const values = parseCSVLine(line).map(v => 
            v.replace(/['"]/g, '').trim()
          );
          const obj: any = {};
          headers.forEach((header, i) => {
            if (values[i] !== undefined) {
              obj[header] = values[i];
            }
          });
          return obj;
        }).filter(row => Object.values(row).some(val => val && val !== ''));

        console.log('Update data parsed:', data.length, 'records');
        console.log('Sample update data:', data.slice(0, 2));
        
        if (data.length === 0) {
          alert('No valid data rows found in the file. Please check that your file contains data rows with values.');
          return;
        }

        console.log('Setting update data and showing preview');
        setUploadedUpdateData(data);
        setShowUpdatePreview(true);
      } catch (error) {
        alert(`Error parsing file: ${error instanceof Error ? error.message : 'Unknown error'}. Please check the file format and try again.`);
      }
    };
    
    reader.readAsText(file, 'UTF-8');
  };

  const processUpdateDataDirectly = async (data: any[]) => {
    try {
      const updates = data.map((row) => {
        const getFieldValue = (possibleHeaders: string[]): string => {
          for (const header of possibleHeaders) {
            const matchingKey = Object.keys(row).find(key =>
              key.toLowerCase().replace(/[^a-z0-9]/g, '') === header.toLowerCase().replace(/[^a-z0-9]/g, '')
            );
            if (matchingKey && row[matchingKey]) {
              return row[matchingKey];
            }
          }
          return '';
        };

        return {
          applicationIdCode: getFieldValue(['Application ID', 'ApplicationID', 'App ID', 'Application Code', 'applicationIdCode']),
          name: getFieldValue(['Application Name', 'Name', 'App Name', 'ApplicationName']),
          itHardwareInfrastructure: getFieldValue(['IT Hardware Infrastructure', 'Infrastructure', 'Hardware']),
          dateOfIntroduction: getFieldValue(['Date of Introduction', 'Introduction Date', 'Date Introduced']),
          applicationOwner: getFieldValue(['Application Owner', 'Owner', 'App Owner']),
          appOwnerEmail: getFieldValue(['App Owner Email', 'Owner Email', 'Application Owner Email']),
          technicalOwnerCustodian: getFieldValue(['Technical Owner', 'Tech Owner', 'Technical Custodian']),
          toEmail: getFieldValue(['Technical Owner Email', 'Tech Owner Email', 'TO Email']),
          businessFunctionDomain: getFieldValue(['Business Function/Domain', 'Business Function', 'Domain', 'Business Domain']),
          applicationType: getFieldValue(['Application Type', 'Type', 'App Type']),
          technologyStack: getFieldValue(['Technology Stack', 'Tech Stack', 'Technology']),
          currentOperating: getFieldValue(['Current Operating Cost', 'Operating Cost', 'Annual Cost']),
          userCount: parseInt(getFieldValue(['User Count', 'Users', 'Number of Users']) || '0'),
          businessCriticality: getFieldValue(['Business Criticality', 'Criticality', 'Critical']),
          technicalHealthStatus: getFieldValue(['Technical Health Status', 'Technical Health', 'Health Status', 'Technical Status']),
          integrationDependencies: getFieldValue(['Integration Dependencies', 'Dependencies', 'Integrations']),
          vendorSupplier: getFieldValue(['Vendor Supplier', 'Vendor', 'Supplier']),
          parentApplicationId: getFieldValue(['Parent Application ID', 'Parent Application', 'Parent App ID', 'ParentApplicationId']),
          applicationDescription: getFieldValue(['Application Description', 'ApplicationDescription', 'App Description', 'Description']),
          internalNumber: getFieldValue(['Internal Number', 'InternalNumber', 'Internal Ref', 'Ref Number']),
          l1BusinessFunctionalArea: getFieldValue(['L1 - Business Functional Area', 'L1 Business Functional Area', 'L1', 'Business Functional Area']),
          l2CapabilityFunction: getFieldValue(['L2 - Capability Function', 'L2 Capability Function', 'L2', 'Capability Function'])
        };
      }).filter(app => app.applicationIdCode);

      if (updates.length === 0) {
        alert('No valid application IDs found. Please ensure your file contains Application ID or Application Code column with valid values.');
        return;
      }

      // Convert applicationIdCode to actual database ID
      const updatesWithId = updates.map(update => {
        const application = state.applications.find(app =>
          app.applicationIdCode === update.applicationIdCode
        );

        if (!application) {
          return null;
        }

        const { applicationIdCode, ...updateData } = update;

        // Convert camelCase fields to snake_case for database
        const dbUpdateData: any = {};
        Object.keys(updateData).forEach(key => {
          const value = (updateData as any)[key];
          if (value !== undefined) {
            switch (key) {
              case 'dateOfIntroduction':
                dbUpdateData['date_of_introduction'] = value;
                break;
              case 'technicalHealthStatus':
                dbUpdateData['technical_health_status'] = value;
                break;
              case 'integrationDependencies':
                dbUpdateData['integration_dependencies'] = value;
                break;
              case 'itHardwareInfrastructure':
                dbUpdateData['it_hardware_infrastructure'] = value;
                break;
              case 'applicationOwner':
                dbUpdateData['application_owner'] = value;
                break;
              case 'appOwnerEmail':
                dbUpdateData['app_owner_email'] = value;
                break;
              case 'technicalOwnerCustodian':
                dbUpdateData['technical_owner_custodian'] = value;
                break;
              case 'toEmail':
                dbUpdateData['to_email'] = value;
                break;
              case 'businessFunctionDomain':
                dbUpdateData['business_function_domain'] = value;
                break;
              case 'applicationType':
                dbUpdateData['application_type'] = value;
                break;
              case 'technologyStack':
                dbUpdateData['technology_stack'] = value;
                break;
              case 'userCount':
                dbUpdateData['user_count'] = value;
                break;
              case 'businessCriticality':
                dbUpdateData['business_criticality'] = value;
                break;
              case 'vendorSupplier':
                dbUpdateData['vendor_supplier'] = value;
                break;
              case 'applicationDescription':
                dbUpdateData['application_description'] = value;
                break;
              case 'internalNumber':
                dbUpdateData['internal_number'] = value;
                break;
              case 'l1BusinessFunctionalArea':
                dbUpdateData['l1_business_functional_area'] = value;
                break;
              case 'l2CapabilityFunction':
                dbUpdateData['l2_capability_function'] = value;
                break;
              case 'parentApplicationId':
                // Convert applicationIdCode to id for parent application
                if (value) {
                  const parentApp = state.applications.find(app => app.applicationIdCode === value || app.id === value);
                  dbUpdateData['parent_application_id'] = parentApp?.id || null;
                } else {
                  dbUpdateData['parent_application_id'] = null;
                }
                break;
              default:
                dbUpdateData[key] = value;
            }
          }
        });

        return { id: application.id, ...dbUpdateData };
      }).filter(update => update !== null);

      if (updatesWithId.length === 0) {
        alert('No matching applications found. Please ensure your Application ID/Code values match existing applications.');
        return;
      }

      console.log('Processing bulk update with data:', updatesWithId);

      const response = await authFetch('/api/applications/bulk-update', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ updates: updatesWithId }),
      });

      if (!response.ok) {
        throw new Error(`Request failed with status code ${response.status}`);
      }

      const result = await response.json();
      console.log('Bulk update result:', result);

      if (result.success) {
        alert(`Successfully updated ${result.updated} applications!`);
        refreshData();
      } else {
        alert(`Update failed: ${result.message}`);
      }

    } catch (error) {
      console.error('Error updating applications:', error);
      alert(`Error updating applications: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const processUpdateData = async () => {
    try {
      const updates = uploadedUpdateData.map((row) => {
        const getFieldValue = (possibleHeaders: string[]): string => {
          for (const header of possibleHeaders) {
            const matchingKey = Object.keys(row).find(key => 
              key.toLowerCase().replace(/[^a-z0-9]/g, '') === header.toLowerCase().replace(/[^a-z0-9]/g, '')
            );
            if (matchingKey && row[matchingKey]) {
              return row[matchingKey];
            }
          }
          return '';
        };

        return {
          applicationIdCode: getFieldValue(['Application ID', 'ApplicationID', 'App ID', 'Application Code', 'applicationIdCode']),
          name: getFieldValue(['Application Name', 'Name', 'App Name', 'ApplicationName']),
          itHardwareInfrastructure: getFieldValue(['IT Hardware Infrastructure', 'Infrastructure', 'Hardware']),
          dateOfIntroduction: getFieldValue(['Date of Introduction', 'Introduction Date', 'Date Introduced']),
          applicationOwner: getFieldValue(['Application Owner', 'Owner', 'App Owner']),
          appOwnerEmail: getFieldValue(['App Owner Email', 'Owner Email', 'Application Owner Email']),
          technicalOwnerCustodian: getFieldValue(['Technical Owner', 'Tech Owner', 'Technical Custodian']),
          toEmail: getFieldValue(['Technical Owner Email', 'Tech Owner Email', 'TO Email']),
          businessFunctionDomain: getFieldValue(['Business Function/Domain', 'Business Function', 'Domain', 'Business Domain']),
          applicationType: getFieldValue(['Application Type', 'Type', 'App Type']),
          technologyStack: getFieldValue(['Technology Stack', 'Tech Stack', 'Technology']),
          currentOperating: getFieldValue(['Current Operating Cost', 'Operating Cost', 'Annual Cost']),
          userCount: parseInt(getFieldValue(['User Count', 'Users', 'Number of Users']) || '0'),
          businessCriticality: getFieldValue(['Business Criticality', 'Criticality', 'Critical']),
          technicalHealthStatus: getFieldValue(['Technical Health Status', 'Technical Health', 'Health Status', 'Technical Status']),
          integrationDependencies: getFieldValue(['Integration Dependencies', 'Dependencies', 'Integrations']),
          vendorSupplier: getFieldValue(['Vendor Supplier', 'Vendor', 'Supplier']),
          parentApplicationId: getFieldValue(['Parent Application ID', 'Parent Application', 'Parent App ID', 'ParentApplicationId']),
          applicationDescription: getFieldValue(['Application Description', 'ApplicationDescription', 'App Description', 'Description']),
          internalNumber: getFieldValue(['Internal Number', 'InternalNumber', 'Internal Ref', 'Ref Number']),
          l1BusinessFunctionalArea: getFieldValue(['L1 - Business Functional Area', 'L1 Business Functional Area', 'L1', 'Business Functional Area']),
          l2CapabilityFunction: getFieldValue(['L2 - Capability Function', 'L2 Capability Function', 'L2', 'Capability Function'])
        };
      }).filter(app => app.applicationIdCode); // Only include rows with valid application ID

      if (updates.length === 0) {
        alert('No valid application IDs found. Please ensure your file contains Application ID or Application Code column with valid values.');
        return;
      }

      // Convert applicationIdCode to actual database ID
      console.log('Available applications in state:', state.applications.map(app => ({ id: app.id, code: app.applicationIdCode, name: app.name })));
      console.log('CSV application codes to match:', updates.map(u => u.applicationIdCode));

      const updatesWithId = updates.map(update => {
        const application = state.applications.find(app =>
          app.applicationIdCode === update.applicationIdCode
        );
        
        if (!application) {
          console.warn(`❌ Application with code "${update.applicationIdCode}" not found in current applications list`);
          console.warn('Available codes:', state.applications.map(app => app.applicationIdCode));
          return null;
        } else {
          console.log(`✅ Found matching application: ${update.applicationIdCode} -> ${application.name}`);
        }

        // Remove applicationIdCode and add the actual database id
        const { applicationIdCode, ...updateData } = update;
        
        // Convert camelCase fields to snake_case for database
        const dbUpdateData: any = {};
        
        console.log('Processing update for application:', application.name);
        console.log('Update data keys:', Object.keys(updateData));
        console.log('Update data values:', updateData);
        
        Object.keys(updateData).forEach(key => {
          const value = (updateData as any)[key];
          console.log(`Field ${key}: "${value}" (type: ${typeof value})`);
          
          // Allow empty strings for optional fields, but skip undefined values
          if (value !== undefined) {
            switch (key) {
              case 'itHardwareInfrastructure':
                dbUpdateData['it_hardware_infrastructure'] = value;
                break;
              case 'userCount':
                dbUpdateData['user_count'] = parseInt(String(value)) || 0;
                break;
              case 'dateOfIntroduction':
                dbUpdateData['date_of_introduction'] = value;
                break;
              case 'applicationOwner':
                dbUpdateData['application_owner'] = value;
                break;
              case 'appOwnerEmail':
                dbUpdateData['app_owner_email'] = value;
                break;
              case 'technicalOwnerCustodian':
                dbUpdateData['technical_owner_custodian'] = value;
                break;
              case 'businessFunctionDomain':
                dbUpdateData['business_function_domain'] = value;
                break;
              case 'applicationType':
                dbUpdateData['application_type'] = value;
                break;
              case 'technologyStack':
                dbUpdateData['technology_stack'] = value;
                break;
              case 'businessCriticality':
                dbUpdateData['business_criticality'] = value;
                break;
              case 'technicalHealthStatus':
                dbUpdateData['technical_health_status'] = value;
                break;
              case 'integrationDependencies':
                dbUpdateData['integration_dependencies'] = value;
                break;
              case 'vendorSupplier':
                dbUpdateData['vendor_supplier'] = value;
                break;
              case 'applicationDescription':
                dbUpdateData['application_description'] = value;
                break;
              case 'internalNumber':
                dbUpdateData['internal_number'] = value;
                break;
              case 'l1BusinessFunctionalArea':
                dbUpdateData['l1_business_functional_area'] = value;
                break;
              case 'l2CapabilityFunction':
                dbUpdateData['l2_capability_function'] = value;
                break;
              case 'parentApplicationId':
                // Convert applicationIdCode to id for parent application
                if (value) {
                  const parentApp = state.applications.find(app => app.applicationIdCode === value || app.id === value);
                  dbUpdateData['parent_application_id'] = parentApp?.id || null;
                } else {
                  dbUpdateData['parent_application_id'] = null;
                }
                break;
              default:
                // For fields that are already in snake_case or don't need conversion
                dbUpdateData[key] = value;
                break;
            }
          }
        });

        return {
          id: application.id,
          ...dbUpdateData
        };
      }).filter(update => update !== null); // Remove any null entries

      if (updatesWithId.length === 0) {
        alert('No matching applications found. Please ensure your Application ID/Code values match existing applications.');
        return;
      }

      if (updatesWithId.length < updates.length) {
        const missingCount = updates.length - updatesWithId.length;
        if (!window.confirm(`${missingCount} applications were not found in the system. Continue with updating ${updatesWithId.length} applications?`)) {
          return;
        }
      }

      // Use bulk update API
      const result = await apiService.bulkUpdateApplications(updatesWithId);

      setUploadedUpdateData([]);
      if (updateFileInputRef.current) {
        updateFileInputRef.current.value = '';
      }

      alert(`Successfully updated ${updatesWithId.length} applications!`);
      
      // Refresh the applications list
      await refreshData();
      
    } catch (error) {
      alert(`Error updating applications: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const downloadUpdateTemplate = () => {
    const headers = [
      'Application ID',
      'Application Name',
      'IT Hardware Infrastructure',
      'Date of Introduction',
      'Application Owner',
      'App Owner Email',
      'Technical Owner',
      'Technical Owner Email',
      'Business Function/Domain',
      'Application Type',
      'Technology Stack',
      'User Count',
      'Business Criticality',
      'Technical Health Status',
      'Integration Dependencies',
      'Vendor/Supplier',
      'Parent Application ID',
      'Application Description',
      'Internal Number',
      'L1 - Business Functional Area',
      'L2 - Capability Function'
    ];

    const csvContent = headers.join(',') + '\n' +
      'APP001,Sample App Updated,Cloud AWS Updated,2020-01-15,John Doe Updated,john.new@company.com,Jane Smith Updated,jane.new@company.com,Finance Updated,Web Application,React/Node.js Updated,160,High,Excellent,Medium,Microsoft Updated,,Core financial reporting platform,REF-001,Finance,Accounts Payable\n' +
      'APP002,Sample App 2 Updated,On-Premise Updated,2019-03-10,Jane Smith Updated,jane.new@company.com,Bob Wilson Updated,bob.new@company.com,HR Updated,Desktop Application,Java/.NET Updated,80,Medium,Good,Low,Oracle Updated,APP001,HR management and payroll system,REF-002,Human Resources,Payroll';

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'application_update_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const downloadApplicationData = () => {
    if (state.applications.length === 0) {
      alert('No applications data to download');
      return;
    }

    const headers = [
      'Application ID',
      'Application Name',
      'IT Hardware Infrastructure',
      'Date of Introduction',
      'Application Owner',
      'App Owner Email',
      'Technical Owner',
      'Technical Owner Email',
      'Business Function/Domain',
      'Application Type',
      'Technology Stack',
      'User Count',
      'Business Criticality',
      'Technical Health Status',
      'Integration Dependencies',
      'Vendor/Supplier',
      'Parent Application ID',
      'Is Parent',
      'Application Description',
      'Internal Number',
      'L1 - Business Functional Area',
      'L2 - Capability Function'
    ];

    const csvRows = [headers.join(',')];

    state.applications.forEach(app => {
      const row = [
        `"${(app.applicationIdCode || '').replace(/"/g, '""')}"`,
        `"${(app.name || '').replace(/"/g, '""')}"`,
        `"${(app.itHardwareInfrastructure || '').replace(/"/g, '""')}"`,
        `"${(app.dateOfIntroduction || '').replace(/"/g, '""')}"`,
        `"${(app.applicationOwner || '').replace(/"/g, '""')}"`,
        `"${(app.appOwnerEmail || '').replace(/"/g, '""')}"`,
        `"${(app.technicalOwnerCustodian || '').replace(/"/g, '""')}"`,
        `"${(app.toEmail || '').replace(/"/g, '""')}"`,
        `"${(app.businessFunctionDomain || '').replace(/"/g, '""')}"`,
        `"${(app.applicationType || '').replace(/"/g, '""')}"`,
        `"${(app.technologyStack || '').replace(/"/g, '""')}"`,
        String(app.userCount || 0),
        `"${(app.businessCriticality || '').replace(/"/g, '""')}"`,
        `"${(app.technicalHealthStatus || '').replace(/"/g, '""')}"`,
        `"${(app.integrationDependencies || '').replace(/"/g, '""')}"`,
        `"${(app.vendorSupplier || '').replace(/"/g, '""')}"`,
        `"${(app.parentApplicationId ? (state.applications.find(p => p.id === app.parentApplicationId)?.applicationIdCode || '') : '').replace(/"/g, '""')}"`,
        app.isParent ? 'Y' : '',
        `"${(app.applicationDescription || '').replace(/"/g, '""')}"`,
        `"${(app.internalNumber || '').replace(/"/g, '""')}"`,
        `"${(app.l1BusinessFunctionalArea || '').replace(/"/g, '""')}"`,
        `"${(app.l2CapabilityFunction || '').replace(/"/g, '""')}"`
      ];
      csvRows.push(row.join(','));
    });

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `application_master_data_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };


  const getCustomerName = (customerId: string) => {
    const customer = state.customers.find(c => c.id === customerId);
    return customer?.name || 'Unknown Customer';
  };

  // Debug logging
  console.log('🔍 ApplicationMasterData: state.applications length:', state.applications.length);
  console.log('🔍 ApplicationMasterData: state.loading:', state.loading);
  if (state.applications.length > 0) {
    console.log('🔍 ApplicationMasterData: First app:', state.applications[0]?.name);
  }

  // Filter applications based on search text
  const filteredApplications = state.applications.filter(application => {
    // Vendor text filter (partial match)
    const matchesVendorFilter = !filterVendor.trim() ||
      (application.vendorSupplier || '').toLowerCase().includes(filterVendor.toLowerCase().trim());

    if (!matchesVendorFilter) return false;

    if (!filterText.trim()) return true;

    const searchText = filterText.toLowerCase();

    const safeIncludes = (value: string | undefined | null): boolean => {
      if (!value) return false;
      return value.toLowerCase().includes(searchText);
    };

    return safeIncludes(application.name) || safeIncludes(application.applicationIdCode) ||
      safeIncludes(application.technologyStack) || safeIncludes(application.applicationOwner) ||
      safeIncludes(application.applicationType) || safeIncludes(application.itHardwareInfrastructure) ||
      safeIncludes(application.vendorSupplier) || safeIncludes(application.technicalOwnerCustodian) ||
      safeIncludes(application.businessCriticality) || safeIncludes(application.technicalHealthStatus) ||
      safeIncludes(application.internalNumber);
  });

  // Expand: when a parent app is in the filtered results, include all its children
  const expandedApplications = (() => {
    if (!filterText.trim()) return filteredApplications;
    const filteredIds = new Set(filteredApplications.map(a => a.id));
    const parentIdsInResults = new Set(
      filteredApplications.filter(a => a.isParent).map(a => a.id)
    );
    if (parentIdsInResults.size === 0) return filteredApplications;
    // Find children from full dataset that belong to matched parents but aren't already in results
    const extraChildren = state.applications.filter(a =>
      a.parentApplicationId &&
      parentIdsInResults.has(a.parentApplicationId) &&
      !filteredIds.has(a.id)
    );
    return [...filteredApplications, ...extraChildren];
  })();

  // Sort: parents with children grouped beneath (ascending by App ID), then standalone apps
  const sortedApplications = (() => {
    const parents = expandedApplications.filter(a => a.isParent);
    const children = expandedApplications.filter(a => a.parentApplicationId && !a.isParent);
    const standalone = expandedApplications.filter(a => !a.isParent && !a.parentApplicationId);

    const result: typeof expandedApplications = [];

    // Add parents with their children grouped beneath — sorted by App ID ascending
    parents.sort((a, b) => (a.applicationIdCode || '').localeCompare(b.applicationIdCode || ''));
    for (const parent of parents) {
      result.push(parent);
      const myChildren = children
        .filter(c => c.parentApplicationId === parent.id)
        .sort((a, b) => (a.applicationIdCode || '').localeCompare(b.applicationIdCode || ''));
      result.push(...myChildren);
    }

    // Add orphan children (parent not in filtered list)
    const placedChildIds = new Set(result.map(a => a.id));
    const orphanChildren = children.filter(c => !placedChildIds.has(c.id));
    result.push(...orphanChildren.sort((a, b) => (a.applicationIdCode || '').localeCompare(b.applicationIdCode || '')));

    // Add standalone apps — sorted by App ID ascending
    result.push(...standalone.sort((a, b) => (a.applicationIdCode || '').localeCompare(b.applicationIdCode || '')));

    return result;
  })();

  // Clear filter function
  const clearFilter = () => {
    setFilterText('');
    setFilterVendor('');
  };

  return (
    <div className="page page-fixed-header">
      {/* Fixed Header Section - Does not scroll */}
      <div className="fixed-header-section">
        {/* Header */}
        <div className="page-header">
          <div className="page-header__content">
            <div className="page-header__title-section">
              <h1 className="page-header__title">Application Master Data</h1>
            </div>
            <div className="page-header__actions">
              <button
                className="btn btn-primary"
                onClick={() => setShowForm(true)}
              >
                ➕ Add Application
              </button>
              <button
                className="btn btn-outline-info"
                onClick={downloadUpdateTemplate}
                title="Download template for updating existing applications"
              >
                📋 Download Template
              </button>
              <button
                className="btn btn-outline-primary"
                onClick={() => fileInputRef.current?.click()}
                title="Upload CSV to create new applications"
              >
                📤 Create from File
              </button>
              <button
                className="btn btn-outline-secondary"
                onClick={() => updateFileInputRef.current?.click()}
                title="Upload CSV to update existing applications"
              >
                📝 Update from File
              </button>
              <button
                className="btn btn-danger"
                onClick={handleDeleteAllData}
                disabled={state.applications.length === 0}
              >
                🗑️ Delete All Data
              </button>
              {state.applications.length > 0 && (
                <button
                  className="btn btn-outline-success"
                  onClick={downloadApplicationData}
                  title="Download current applications data as CSV"
                  style={{ border: '2px solid #28a745' }}
                >
                  📥 Download Data
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Statistics - Right below header */}
        {(() => {
          const childApplications = filteredApplications.filter(app => !app.isParent);
          const parentApplications = filteredApplications.filter(app => app.isParent);
          return (
            <div className="master-data-stats">
              <div className="stat-item">
                <span className="stat-value">{formatNumber(childApplications.length)}</span>
                <span className="stat-label">{filterText ? 'Filtered' : 'Total'} Applications</span>
              </div>
              <div className="stat-item">
                <span className="stat-value">{formatNumber(parentApplications.length)}</span>
                <span className="stat-label">Parent Applications</span>
              </div>
              <div className="stat-item">
                <span className="stat-value">{formatNumber(childApplications.reduce((sum, app) => sum + (app.userCount || 0), 0))}</span>
                <span className="stat-label">Total Users</span>
              </div>
              <div className="stat-item">
                <span className="stat-value">{formatNumber(new Set(childApplications.map(app => app.businessFunctionDomain).filter(Boolean)).size)}</span>
                <span className="stat-label">Business Domains</span>
              </div>
            </div>
          );
        })()}

        {/* Filter Section - Below Statistics */}
        <div style={{ marginBottom: '0', display: 'flex', gap: '12px', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #dee2e6' }}>
          <div style={{ position: 'relative', flex: '1', maxWidth: '400px' }}>
            <input
              type="text"
              placeholder="Filter applications by name, ID, technology, owner, type, infrastructure..."
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              style={{
                width: '100%',
                padding: '6px 30px 6px 10px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '15px'
              }}
            />
            {filterText && (
              <button
                onClick={clearFilter}
                style={{
                  position: 'absolute',
                  right: '8px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '14px',
                  color: '#666'
                }}
                title="Clear filter"
              >
                ×
              </button>
            )}
          </div>
          <div style={{ position: 'relative', maxWidth: '250px' }}>
            <input
              type="text"
              placeholder="Filter by vendor..."
              value={filterVendor}
              onChange={(e) => setFilterVendor(e.target.value)}
              style={{
                width: '100%',
                padding: '6px 30px 6px 10px',
                border: filterVendor ? '2px solid #1976d2' : '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '15px'
              }}
            />
            {filterVendor && (
              <button
                onClick={() => setFilterVendor('')}
                style={{
                  position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)',
                  background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px', color: '#666'
                }}
                title="Clear vendor filter"
              >×</button>
            )}
          </div>
          {(filterText || filterVendor) && (
            <div style={{ fontSize: '13px', color: '#666' }}>
              Showing {formatNumber(sortedApplications.length)} of {formatNumber(state.applications.length)} applications
            </div>
          )}
        </div>
      </div>

      {/* Hidden file inputs */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileUpload}
        accept=".csv"
        style={{ display: 'none' }}
      />
      <input
        type="file"
        ref={updateFileInputRef}
        onChange={handleUpdateFileUpload}
        accept=".csv"
        style={{ display: 'none' }}
      />

      {/* Upload Preview Modal */}
      {showPreview && (
        <div className="modal-overlay" style={{ zIndex: 1100 }} onClick={() => { setShowPreview(false); setUploadedData([]); }}>
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white',
              borderRadius: '12px',
              padding: '24px',
              minWidth: '800px',
              maxWidth: '95vw',
              maxHeight: '90vh',
              overflow: 'auto',
              boxShadow: '0 4px 6px rgba(0,0,0,0.1), 0 8px 24px rgba(0,0,0,0.15)',
              position: 'relative',
              zIndex: 1101
            }}
          >
            <div style={{ marginBottom: '24px' }}>
              <h2 style={{
                margin: '0 0 8px 0',
                fontSize: '24px',
                fontWeight: '500',
                color: '#1a1a1a'
              }}>
                Preview Upload Data ({uploadedData.length} records)
              </h2>
              <p style={{
                margin: 0,
                fontSize: '14px',
                color: '#666'
              }}>
                Review the data below before importing
              </p>
            </div>

            <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '8px', fontSize: '12px' }}>
              <strong>Available headers:</strong> {Object.keys(uploadedData[0] || {}).join(', ')}
            </div>

            <div style={{ overflowX: 'auto', marginBottom: '24px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8f9fa' }}>
                    <th style={{ padding: '12px 8px', textAlign: 'left', borderBottom: '2px solid #dee2e6', fontWeight: '600' }}>Application ID</th>
                    <th style={{ padding: '12px 8px', textAlign: 'left', borderBottom: '2px solid #dee2e6', fontWeight: '600' }}>Application Name</th>
                    <th style={{ padding: '12px 8px', textAlign: 'left', borderBottom: '2px solid #dee2e6', fontWeight: '600' }}>Technology</th>
                    <th style={{ padding: '12px 8px', textAlign: 'left', borderBottom: '2px solid #dee2e6', fontWeight: '600' }}>Users</th>
                    <th style={{ padding: '12px 8px', textAlign: 'left', borderBottom: '2px solid #dee2e6', fontWeight: '600' }}>Infrastructure</th>
                    <th style={{ padding: '12px 8px', textAlign: 'left', borderBottom: '2px solid #dee2e6', fontWeight: '600' }}>Owner</th>
                    <th style={{ padding: '12px 8px', textAlign: 'left', borderBottom: '2px solid #dee2e6', fontWeight: '600' }}>Type</th>
                  </tr>
                </thead>
                <tbody>
                  {uploadedData.map((row, index) => {
                    const getPreviewValue = (possibleHeaders: string[]): string => {
                      for (const header of possibleHeaders) {
                        if (row[header] !== undefined && row[header] !== null) {
                          return String(row[header]).trim() || 'N/A';
                        }
                      }
                      return 'N/A';
                    };

                    return (
                      <tr key={index} style={{ borderBottom: '1px solid #dee2e6' }}>
                        <td style={{ padding: '10px 8px' }}>{getPreviewValue(['Application ID/Code', 'Application ID', 'App ID', 'ID', 'ApplicationID', 'Application IDCode'])}</td>
                        <td style={{ padding: '10px 8px' }}>{getPreviewValue(['Application Name', 'ApplicationName', 'App Name', 'name'])}</td>
                        <td style={{ padding: '10px 8px' }}>{getPreviewValue(['Technology Stack', 'Technology', 'Tech Stack', 'TechnologyStack'])}</td>
                        <td style={{ padding: '10px 8px' }}>{getPreviewValue(['User Count', 'Users', 'UserCount', 'User'])}</td>
                        <td style={{ padding: '10px 8px' }}>{getPreviewValue(['IT Hardware/Infrastructure', 'IT HardwareInfrastructure', 'IT Hardware Infrastructure', 'Infrastructure', 'Hardware', 'ITHardwareInfrastructure'])}</td>
                        <td style={{ padding: '10px 8px' }}>{getPreviewValue(['Application Owner', 'Owner', 'App Owner', 'ApplicationOwner'])}</td>
                        <td style={{ padding: '10px 8px' }}>{getPreviewValue(['Application Type', 'Type', 'App Type', 'ApplicationType'])}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
              <button
                type="button"
                onClick={() => { setShowPreview(false); setUploadedData([]); }}
                style={{
                  padding: '10px 24px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#666',
                  backgroundColor: 'transparent',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={processUploadedData}
                style={{
                  padding: '10px 24px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'white',
                  backgroundColor: '#1976d2',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#1565c0'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#1976d2'}
              >
                Import {uploadedData.length} Applications
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Update Preview Modal */}

      {/* Delete All Data Modal */}
      {showDeleteModal && (
        <div className="form-modal" style={{ zIndex: 1100 }}>
          <div className="form-modal-content" style={{ zIndex: 1101 }}>
            <div className="form-modal-header">
              <h2>⚠️ Delete All Application Data</h2>
              <button 
                className="btn-close"
                onClick={handleCancelDeleteAll}
                aria-label="Close modal"
              >
                ×
              </button>
            </div>
            <div className="delete-modal-content">
              <div className="warning-message">
                <div className="warning-icon">🚨</div>
                <div className="warning-text">
                  <h3>CRITICAL ACTION</h3>
                  <p>You are about to permanently delete <strong>{state.applications.length} applications</strong> from the database.</p>
                  <p><strong>⚠️ Note:</strong> Applications that are part of APR models, have assessment data, or are in APR runs will NOT be deleted and will show as errors.</p>
                  <p>This action cannot be undone and will remove:</p>
                  <ul>
                    <li>All application master data (for standalone applications)</li>
                    <li>All associated cost information</li>
                    <li>Applications not linked to any APR processes</li>
                  </ul>
                  <p><strong>Protected applications:</strong> Those assigned to APR models or with assessment data will be skipped for safety.</p>
                </div>
              </div>
              
              <div className="password-section">
                <label htmlFor="deleteConfirmText">Type <strong>DELETE</strong> to confirm:</label>
                <input
                  type="text"
                  id="deleteConfirmText"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder="Type DELETE to confirm"
                  className="password-input"
                  autoFocus
                  style={{ textTransform: 'uppercase' }}
                />
                <small className="password-hint">
                  This action requires typing "DELETE" to proceed.
                </small>
              </div>

              <div className="delete-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCancelDeleteAll}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleConfirmDeleteAll}
                  disabled={deleteConfirmText !== 'DELETE'}
                >
                  🗑️ Delete All Data
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Application Form Modal */}
      {showForm && (
        <div className="modal-overlay" onClick={resetForm}>
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white',
              borderRadius: '12px',
              padding: '24px',
              minWidth: '700px',
              maxWidth: '900px',
              maxHeight: '90vh',
              overflow: 'auto',
              boxShadow: '0 4px 6px rgba(0,0,0,0.1), 0 8px 24px rgba(0,0,0,0.15)',
              position: 'relative'
            }}
          >
            <div style={{ marginBottom: '24px' }}>
              <h2 style={{
                margin: '0 0 8px 0',
                fontSize: '24px',
                fontWeight: '500',
                color: '#1a1a1a'
              }}>
                {editingApplication ? 'Edit Application' : 'Add New Application'}
              </h2>
              <p style={{
                margin: 0,
                fontSize: '14px',
                color: '#666'
              }}>
                {editingApplication ? 'Update application information' : 'Add a new application to the portfolio'}
              </p>
            </div>

            <form onSubmit={handleSubmit}>
              {/* Basic Information */}
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{
                  margin: '0 0 16px 0',
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#333',
                  borderBottom: '1px solid #eee',
                  paddingBottom: '8px'
                }}>Basic Information</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div style={{ marginBottom: '16px' }}>
                    <label htmlFor="name" style={{
                      display: 'block',
                      marginBottom: '8px',
                      fontSize: '14px',
                      fontWeight: '500',
                      color: '#333'
                    }}>
                      Application Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      readOnly={!!editingApplication}
                      style={{
                        width: '100%',
                        padding: '12px',
                        fontSize: '14px',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        boxSizing: 'border-box',
                        backgroundColor: editingApplication ? '#f5f5f5' : 'white',
                        cursor: editingApplication ? 'not-allowed' : 'text'
                      }}
                      placeholder="Enter application name"
                    />
                  </div>
                  <div style={{ marginBottom: '16px' }}>
                    <label htmlFor="applicationIdCode" style={{
                      display: 'block',
                      marginBottom: '8px',
                      fontSize: '14px',
                      fontWeight: '500',
                      color: '#333'
                    }}>
                      Application ID/Code
                    </label>
                    <input
                      type="text"
                      id="applicationIdCode"
                      name="applicationIdCode"
                      value={formData.applicationIdCode}
                      onChange={handleInputChange}
                      readOnly={!!editingApplication}
                      style={{
                        width: '100%',
                        padding: '12px',
                        fontSize: '14px',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        boxSizing: 'border-box',
                        backgroundColor: editingApplication ? '#f5f5f5' : 'white',
                        cursor: editingApplication ? 'not-allowed' : 'text'
                      }}
                      placeholder="Enter application ID"
                    />
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div style={{ marginBottom: '16px' }}>
                    <label htmlFor="applicationType" style={{
                      display: 'block',
                      marginBottom: '8px',
                      fontSize: '14px',
                      fontWeight: '500',
                      color: '#333'
                    }}>
                      Application Type
                    </label>
                    <input
                      type="text"
                      id="applicationType"
                      name="applicationType"
                      value={formData.applicationType}
                      onChange={handleInputChange}
                      style={{
                        width: '100%',
                        padding: '12px',
                        fontSize: '14px',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        boxSizing: 'border-box'
                      }}
                      placeholder="Enter application type"
                    />
                  </div>
                  <div style={{ marginBottom: '16px' }}>
                    <label htmlFor="dateOfIntroduction" style={{
                      display: 'block',
                      marginBottom: '8px',
                      fontSize: '14px',
                      fontWeight: '500',
                      color: '#333'
                    }}>
                      Date of Introduction
                    </label>
                    <input
                      type="date"
                      id="dateOfIntroduction"
                      name="dateOfIntroduction"
                      value={formData.dateOfIntroduction}
                      onChange={handleInputChange}
                      style={{
                        width: '100%',
                        padding: '12px',
                        fontSize: '14px',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <label htmlFor="parentApplicationId" style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#333'
                  }}>
                    Parent Application
                  </label>
                  <select
                    id="parentApplicationId"
                    name="parentApplicationId"
                    value={formData.parentApplicationId}
                    onChange={handleInputChange}
                    style={{
                      width: '100%',
                      padding: '12px',
                      fontSize: '14px',
                      border: '1px solid #ddd',
                      borderRadius: '8px',
                      boxSizing: 'border-box',
                      backgroundColor: 'white'
                    }}
                  >
                    <option value="">-- No Parent Application --</option>
                    {state.applications
                      .filter(app => app.id !== editingApplication?.id)
                      .map(app => (
                        <option key={app.id} value={app.id}>
                          {app.name} {app.applicationIdCode ? `(${app.applicationIdCode})` : ''}
                        </option>
                      ))}
                  </select>
                </div>
              </div>

              {/* Technical Information */}
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{
                  margin: '0 0 16px 0',
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#333',
                  borderBottom: '1px solid #eee',
                  paddingBottom: '8px'
                }}>Technical Information</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
                  <div style={{ marginBottom: '16px' }}>
                    <label htmlFor="itHardwareInfrastructure" style={{
                      display: 'block',
                      marginBottom: '8px',
                      fontSize: '14px',
                      fontWeight: '500',
                      color: '#333'
                    }}>
                      IT Hardware/Infrastructure
                    </label>
                    <input
                      type="text"
                      id="itHardwareInfrastructure"
                      name="itHardwareInfrastructure"
                      value={formData.itHardwareInfrastructure}
                      onChange={handleInputChange}
                      style={{
                        width: '100%',
                        padding: '12px',
                        fontSize: '14px',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        boxSizing: 'border-box'
                      }}
                      placeholder="Enter infrastructure"
                    />
                  </div>
                  <div style={{ marginBottom: '16px' }}>
                    <label htmlFor="technologyStack" style={{
                      display: 'block',
                      marginBottom: '8px',
                      fontSize: '14px',
                      fontWeight: '500',
                      color: '#333'
                    }}>
                      Technology Stack
                    </label>
                    <input
                      type="text"
                      id="technologyStack"
                      name="technologyStack"
                      value={formData.technologyStack}
                      onChange={handleInputChange}
                      style={{
                        width: '100%',
                        padding: '12px',
                        fontSize: '14px',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        boxSizing: 'border-box'
                      }}
                      placeholder="Enter tech stack"
                    />
                  </div>
                  <div style={{ marginBottom: '16px' }}>
                    <label htmlFor="vendorSupplier" style={{
                      display: 'block',
                      marginBottom: '8px',
                      fontSize: '14px',
                      fontWeight: '500',
                      color: '#333'
                    }}>
                      Vendor/Supplier
                    </label>
                    <input
                      type="text"
                      id="vendorSupplier"
                      name="vendorSupplier"
                      value={formData.vendorSupplier}
                      onChange={handleInputChange}
                      style={{
                        width: '100%',
                        padding: '12px',
                        fontSize: '14px',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        boxSizing: 'border-box'
                      }}
                      placeholder="Enter vendor"
                    />
                  </div>
                </div>
              </div>

              {/* Ownership Information */}
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{
                  margin: '0 0 16px 0',
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#333',
                  borderBottom: '1px solid #eee',
                  paddingBottom: '8px'
                }}>Ownership Information</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div style={{ marginBottom: '16px' }}>
                    <label htmlFor="applicationOwner" style={{
                      display: 'block',
                      marginBottom: '8px',
                      fontSize: '14px',
                      fontWeight: '500',
                      color: '#333'
                    }}>
                      Application Owner
                    </label>
                    <input
                      type="text"
                      id="applicationOwner"
                      name="applicationOwner"
                      value={formData.applicationOwner}
                      onChange={handleInputChange}
                      style={{
                        width: '100%',
                        padding: '12px',
                        fontSize: '14px',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        boxSizing: 'border-box'
                      }}
                      placeholder="Enter owner name"
                    />
                  </div>
                  <div style={{ marginBottom: '16px' }}>
                    <label htmlFor="appOwnerEmail" style={{
                      display: 'block',
                      marginBottom: '8px',
                      fontSize: '14px',
                      fontWeight: '500',
                      color: '#333'
                    }}>
                      App Owner Email
                    </label>
                    <input
                      type="email"
                      id="appOwnerEmail"
                      name="appOwnerEmail"
                      value={formData.appOwnerEmail}
                      onChange={handleInputChange}
                      style={{
                        width: '100%',
                        padding: '12px',
                        fontSize: '14px',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        boxSizing: 'border-box'
                      }}
                      placeholder="Enter owner email"
                    />
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div style={{ marginBottom: '16px' }}>
                    <label htmlFor="technicalOwnerCustodian" style={{
                      display: 'block',
                      marginBottom: '8px',
                      fontSize: '14px',
                      fontWeight: '500',
                      color: '#333'
                    }}>
                      Technical Owner/Custodian
                    </label>
                    <input
                      type="text"
                      id="technicalOwnerCustodian"
                      name="technicalOwnerCustodian"
                      value={formData.technicalOwnerCustodian}
                      onChange={handleInputChange}
                      style={{
                        width: '100%',
                        padding: '12px',
                        fontSize: '14px',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        boxSizing: 'border-box'
                      }}
                      placeholder="Enter technical owner"
                    />
                  </div>
                  <div style={{ marginBottom: '16px' }}>
                    <label htmlFor="toEmail" style={{
                      display: 'block',
                      marginBottom: '8px',
                      fontSize: '14px',
                      fontWeight: '500',
                      color: '#333'
                    }}>
                      Technical Owner Email
                    </label>
                    <input
                      type="email"
                      id="toEmail"
                      name="toEmail"
                      value={formData.toEmail}
                      onChange={handleInputChange}
                      style={{
                        width: '100%',
                        padding: '12px',
                        fontSize: '14px',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        boxSizing: 'border-box'
                      }}
                      placeholder="Enter technical owner email"
                    />
                  </div>
                </div>
              </div>

              {/* Business Information */}
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{
                  margin: '0 0 16px 0',
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#333',
                  borderBottom: '1px solid #eee',
                  paddingBottom: '8px'
                }}>Business Information</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div style={{ marginBottom: '16px' }}>
                    <label htmlFor="businessFunctionDomain" style={{
                      display: 'block',
                      marginBottom: '8px',
                      fontSize: '14px',
                      fontWeight: '500',
                      color: '#333'
                    }}>
                      Business Function/Domain
                    </label>
                    <input
                      type="text"
                      id="businessFunctionDomain"
                      name="businessFunctionDomain"
                      value={formData.businessFunctionDomain}
                      onChange={handleInputChange}
                      style={{
                        width: '100%',
                        padding: '12px',
                        fontSize: '14px',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        boxSizing: 'border-box'
                      }}
                      placeholder="Enter business function"
                    />
                  </div>
                  <div style={{ marginBottom: '16px' }}>
                    <label htmlFor="userCount" style={{
                      display: 'block',
                      marginBottom: '8px',
                      fontSize: '14px',
                      fontWeight: '500',
                      color: '#333'
                    }}>
                      User Count
                    </label>
                    <input
                      type="number"
                      id="userCount"
                      name="userCount"
                      value={formData.userCount}
                      onChange={handleInputChange}
                      min="0"
                      style={{
                        width: '100%',
                        padding: '12px',
                        fontSize: '14px',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        boxSizing: 'border-box'
                      }}
                      placeholder="Enter user count"
                    />
                  </div>
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <label htmlFor="businessCriticality" style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#333'
                  }}>
                    Business Criticality
                  </label>
                  <input
                    type="text"
                    id="businessCriticality"
                    name="businessCriticality"
                    value={formData.businessCriticality}
                    onChange={handleInputChange}
                    style={{
                      width: '100%',
                      padding: '12px',
                      fontSize: '14px',
                      border: '1px solid #ddd',
                      borderRadius: '8px',
                      boxSizing: 'border-box'
                    }}
                    placeholder="Enter business criticality"
                  />
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <label htmlFor="technicalHealthStatus" style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#333'
                  }}>
                    Technical Health Status
                  </label>
                  <input
                    type="text"
                    id="technicalHealthStatus"
                    name="technicalHealthStatus"
                    value={formData.technicalHealthStatus}
                    onChange={handleInputChange}
                    style={{
                      width: '100%',
                      padding: '12px',
                      fontSize: '14px',
                      border: '1px solid #ddd',
                      borderRadius: '8px',
                      boxSizing: 'border-box'
                    }}
                    placeholder="Enter health status"
                  />
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <label htmlFor="integrationDependencies" style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#333'
                  }}>
                    Integration Dependencies
                  </label>
                  <input
                    type="text"
                    id="integrationDependencies"
                    name="integrationDependencies"
                    value={formData.integrationDependencies}
                    onChange={handleInputChange}
                    style={{
                      width: '100%',
                      padding: '12px',
                      fontSize: '14px',
                      border: '1px solid #ddd',
                      borderRadius: '8px',
                      boxSizing: 'border-box'
                    }}
                    placeholder="Enter dependencies"
                  />
                </div>
              </div>

              {/* Application Description */}
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{
                  margin: '0 0 16px 0',
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#333',
                  borderBottom: '1px solid #eee',
                  paddingBottom: '8px'
                }}>Application Description</h3>
                <div style={{ marginBottom: '16px' }}>
                  <textarea
                    id="applicationDescription"
                    name="applicationDescription"
                    value={formData.applicationDescription}
                    onChange={handleInputChange}
                    rows={4}
                    style={{
                      width: '100%',
                      padding: '12px',
                      fontSize: '14px',
                      border: '1px solid #ddd',
                      borderRadius: '8px',
                      boxSizing: 'border-box',
                      resize: 'vertical'
                    }}
                    placeholder="Enter a description of the application's purpose and functionality"
                  />
                </div>
              </div>

              {/* Additional Reference Fields */}
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{
                  margin: '0 0 16px 0',
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#333',
                  borderBottom: '1px solid #eee',
                  paddingBottom: '8px'
                }}>Additional Reference</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
                  <div>
                    <label htmlFor="internalNumber" style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#555' }}>Internal Number</label>
                    <input
                      type="text"
                      id="internalNumber"
                      name="internalNumber"
                      value={formData.internalNumber}
                      onChange={handleInputChange}
                      maxLength={18}
                      style={{ width: '100%', padding: '10px 12px', fontSize: '14px', border: '1px solid #ddd', borderRadius: '8px', boxSizing: 'border-box' }}
                      placeholder="e.g., REF-001234"
                    />
                  </div>
                  <div>
                    <label htmlFor="l1BusinessFunctionalArea" style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#555' }}>L1 - Business Functional Area</label>
                    <input
                      type="text"
                      id="l1BusinessFunctionalArea"
                      name="l1BusinessFunctionalArea"
                      value={formData.l1BusinessFunctionalArea}
                      onChange={handleInputChange}
                      maxLength={60}
                      style={{ width: '100%', padding: '10px 12px', fontSize: '14px', border: '1px solid #ddd', borderRadius: '8px', boxSizing: 'border-box' }}
                      placeholder="e.g., Finance"
                    />
                  </div>
                  <div>
                    <label htmlFor="l2CapabilityFunction" style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#555' }}>L2 - Capability Function</label>
                    <input
                      type="text"
                      id="l2CapabilityFunction"
                      name="l2CapabilityFunction"
                      value={formData.l2CapabilityFunction}
                      onChange={handleInputChange}
                      maxLength={60}
                      style={{ width: '100%', padding: '10px 12px', fontSize: '14px', border: '1px solid #ddd', borderRadius: '8px', boxSizing: 'border-box' }}
                      placeholder="e.g., Accounts Payable"
                    />
                  </div>
                </div>
              </div>

              {/* Audit Information - only shown when editing */}
              {editingApplication && (
                <div style={{ marginBottom: '24px' }}>
                  <h3 style={{
                    margin: '0 0 16px 0',
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#333',
                    borderBottom: '1px solid #eee',
                    paddingBottom: '8px'
                  }}>Audit Information</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#666', marginBottom: '4px' }}>Created By</label>
                      <div style={{ fontSize: '14px', color: '#333', padding: '8px 12px', backgroundColor: '#f8f9fa', borderRadius: '6px', border: '1px solid #e0e0e0' }}>{editingApplication.createdByName || 'N/A'}</div>
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#666', marginBottom: '4px' }}>Created On</label>
                      <div style={{ fontSize: '14px', color: '#333', padding: '8px 12px', backgroundColor: '#f8f9fa', borderRadius: '6px', border: '1px solid #e0e0e0' }}>
                        {editingApplication.createdAt
                          ? new Date(editingApplication.createdAt).toLocaleString()
                          : 'N/A'}
                      </div>
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#666', marginBottom: '4px' }}>Updated By</label>
                      <div style={{ fontSize: '14px', color: '#333', padding: '8px 12px', backgroundColor: '#f8f9fa', borderRadius: '6px', border: '1px solid #e0e0e0' }}>{editingApplication.updatedByName || 'N/A'}</div>
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#666', marginBottom: '4px' }}>Updated On</label>
                      <div style={{ fontSize: '14px', color: '#333', padding: '8px 12px', backgroundColor: '#f8f9fa', borderRadius: '6px', border: '1px solid #e0e0e0' }}>
                        {editingApplication.updatedAt
                          ? new Date(editingApplication.updatedAt).toLocaleString()
                          : 'N/A'}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div style={{
                display: 'flex',
                gap: '12px',
                justifyContent: 'flex-end',
                marginTop: '24px',
                paddingTop: '16px',
                borderTop: '1px solid #eee'
              }}>
                <button
                  type="button"
                  onClick={resetForm}
                  style={{
                    padding: '10px 24px',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#666',
                    backgroundColor: 'transparent',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    padding: '10px 24px',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: 'white',
                    backgroundColor: '#1976d2',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#1565c0'}
                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#1976d2'}
                >
                  {editingApplication ? 'Update Application' : 'Create Application'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Scrollable Data Section */}
      <div className="scrollable-data-section" style={{ overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {/* Application Master Data Table */}
        {state.applications.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">📱</div>
            <h3>No Applications Yet</h3>
            <p>Upload CSV files to create new applications or update existing ones, or manually add applications to get started.</p>
            <div className="empty-state-actions" style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button 
                className="btn btn-outline-primary"
                onClick={() => fileInputRef.current?.click()}
              >
                📤 Create from File
              </button>
              <button 
                className="btn btn-outline-secondary"
                onClick={() => updateFileInputRef.current?.click()}
                title="Upload CSV to update existing applications"
              >
                📝 Update from File
              </button>
              <button 
                className="btn btn-primary"
                onClick={() => setShowForm(true)}
              >
                Add Application
              </button>
            </div>
          </div>
        ) : sortedApplications.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">🔍</div>
            <h3>No Applications Found</h3>
            <p>No applications match your current filter criteria.</p>
            <div className="empty-state-actions">
              <button 
                className="btn btn-outline-primary"
                onClick={clearFilter}
              >
                Clear Filter
              </button>
            </div>
          </div>
        ) : (
          <div style={{ border: '1px solid #dee2e6', borderRadius: '0.5rem', backgroundColor: '#fff', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
            {/* Fixed header */}
            <div style={{ flexShrink: 0 }}>
              <table className="master-data-table" style={{ tableLayout: 'fixed', width: '100%' }}>
                <colgroup>
                  <col style={{ width: '9%' }} />
                  <col style={{ width: '22%' }} />
                  <col style={{ width: '10%' }} />
                  <col style={{ width: '10%' }} />
                  <col style={{ width: '5%' }} />
                  <col style={{ width: '12%' }} />
                  <col style={{ width: '11%' }} />
                  <col style={{ width: '9%' }} />
                  <col style={{ width: '12%' }} />
                </colgroup>
                <thead>
                  <tr>
                    {['Application ID', 'Application Name', 'Internal Number', 'Technology', 'Users', 'IT Hardware / Infra', 'Owner', 'Type', 'Actions'].map((label, i) => (
                      <th key={i} style={{
                        backgroundColor: '#e8e8e8',
                        borderBottom: '2px solid #bbb',
                        padding: '10px 8px',
                        fontWeight: 700,
                        fontSize: '13px',
                        color: '#333',
                        textAlign: i === 1 ? 'left' : undefined
                      }}>
                        {label}
                      </th>
                    ))}
                  </tr>
                </thead>
              </table>
            </div>
            {/* Scrollable body */}
            <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
              <table className="master-data-table" style={{ tableLayout: 'fixed', width: '100%' }}>
                <colgroup>
                  <col style={{ width: '9%' }} />
                  <col style={{ width: '22%' }} />
                  <col style={{ width: '10%' }} />
                  <col style={{ width: '10%' }} />
                  <col style={{ width: '5%' }} />
                  <col style={{ width: '12%' }} />
                  <col style={{ width: '11%' }} />
                  <col style={{ width: '9%' }} />
                  <col style={{ width: '12%' }} />
                </colgroup>
              <tbody>
                {sortedApplications.map(application => (
                  <tr
                    key={application.id}
                    style={application.isParent ? { backgroundColor: '#bbdefb', fontWeight: 600 } : application.parentApplicationId ? { backgroundColor: '#f5f5f5' } : undefined}
                  >
                    <td
                      title={application.applicationDescription || ''}
                      style={{ cursor: application.applicationDescription ? 'help' : 'default', position: 'relative' }}
                    >
                      {application.applicationIdCode || 'N/A'}
                      {application.applicationDescription && (
                        <span style={{ marginLeft: '4px', fontSize: '11px', color: '#1976d2' }}>i</span>
                      )}
                    </td>
                    <td style={{ textAlign: 'left' }}>
                      <div className="app-name-cell" style={{ alignItems: 'flex-start', textAlign: 'left' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <strong>{application.name?.trim()}</strong>
                          {application.isParent && (
                            <span style={{
                              padding: '2px 8px',
                              fontSize: '11px',
                              fontWeight: '600',
                              backgroundColor: '#1976d2',
                              color: 'white',
                              borderRadius: '12px'
                            }}>
                              Parent{(() => { const count = state.applications.filter(a => a.parentApplicationId === application.id).length; return count ? ` (${count} children)` : ''; })()}
                            </span>
                          )}
                          {!application.isParent && application.parentApplicationId && (
                            <span style={{
                              padding: '2px 8px',
                              fontSize: '10px',
                              fontWeight: '500',
                              backgroundColor: '#e3f2fd',
                              color: '#1565c0',
                              borderRadius: '12px'
                            }}>
                              Child of {state.applications.find(a => a.id === application.parentApplicationId)?.applicationIdCode || ''}
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td>{application.parentApplicationId ? (state.applications.find(a => a.id === application.parentApplicationId)?.internalNumber || '—') : (application.internalNumber || '—')}</td>
                    <td>{application.parentApplicationId ? (state.applications.find(a => a.id === application.parentApplicationId)?.technologyStack || 'N/A') : (application.technologyStack || 'N/A')}</td>
                    <td>{application.userCount || 0}</td>
                    <td>{application.itHardwareInfrastructure || 'N/A'}</td>
                    <td>{application.applicationOwner || 'N/A'}</td>
                    <td>{application.parentApplicationId ? (state.applications.find(a => a.id === application.parentApplicationId)?.applicationType || 'N/A') : (application.applicationType || 'N/A')}</td>
                    <td>
                      <div style={{ display: 'flex', gap: '4px', justifyContent: 'center', alignItems: 'center' }}>
                        <button
                          className="action-btn view-btn"
                          onClick={() => handleView(application)}
                          title="View application"
                          style={{
                            padding: '4px 6px',
                            backgroundColor: 'transparent',
                            color: '#1976d2',
                            border: '1px solid #1976d2',
                            borderRadius: '4px',
                            fontSize: '14px',
                            cursor: 'pointer',
                            minWidth: 'auto'
                          }}
                        >
                          👁️
                        </button>
                        <button
                          className="action-btn edit-btn"
                          onClick={() => handleEdit(application)}
                          title="Edit application"
                          style={{
                            padding: '4px 6px',
                            backgroundColor: 'transparent',
                            color: '#1976d2',
                            border: '1px solid #1976d2',
                            borderRadius: '4px',
                            fontSize: '14px',
                            cursor: 'pointer',
                            minWidth: 'auto'
                          }}
                        >
                          ✏️
                        </button>
                        <button
                          className="action-btn delete-btn"
                          onClick={() => handleDelete(application.id)}
                          title="Delete application"
                          style={{
                            padding: '4px 6px',
                            backgroundColor: 'transparent',
                            color: '#d32f2f',
                            border: '1px solid #d32f2f',
                            borderRadius: '4px',
                            fontSize: '14px',
                            cursor: 'pointer',
                            minWidth: 'auto'
                          }}
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
        )}
      </div>

      {/* Update Preview Modal */}
      {showUpdatePreview && (
        <div className="form-modal" style={{ zIndex: 1100 }}>
          <div className="form-modal-content large" style={{ zIndex: 1101 }}>
            <div className="form-modal-header">
              <h2>Preview Update Data ({uploadedUpdateData.length} records)</h2>
              <button 
                className="btn-close"
                onClick={() => { setShowUpdatePreview(false); setUploadedUpdateData([]); }}
              >
                ×
              </button>
            </div>
            <div className="upload-preview">
              <div className="preview-summary">
                <span className="preview-count">{uploadedUpdateData.length} applications ready for update</span>
              </div>
              <div className="preview-table-container">
                <table className="preview-table">
                  <thead>
                    <tr>
                      <th>App ID</th>
                      <th>Application Name</th>
                      <th>Infrastructure</th>
                      <th>Date of Introduction</th>
                      <th>Technical Health</th>
                      <th>Integration Dependencies</th>
                      <th>Owner</th>
                      <th>Technology Stack</th>
                      <th>User Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {uploadedUpdateData.slice(0, 10).map((row, index) => {
                      const getPreviewValue = (possibleHeaders: string[]): string => {
                        for (const header of possibleHeaders) {
                          const matchingKey = Object.keys(row).find(key => 
                            key.toLowerCase().replace(/[^a-z0-9]/g, '') === header.toLowerCase().replace(/[^a-z0-9]/g, '')
                          );
                          if (matchingKey && row[matchingKey]) {
                            return row[matchingKey];
                          }
                        }
                        return '';
                      };

                      return (
                        <tr key={index}>
                          <td>{getPreviewValue(['Application ID', 'ApplicationID', 'App ID', 'Application Code'])}</td>
                          <td>{getPreviewValue(['Application Name', 'Name', 'App Name'])}</td>
                          <td>{getPreviewValue(['IT Hardware Infrastructure', 'Infrastructure', 'Hardware'])}</td>
                          <td>{getPreviewValue(['Date of Introduction', 'Introduction Date', 'Date Introduced'])}</td>
                          <td>{getPreviewValue(['Technical Health Status', 'Technical Health', 'Health Status'])}</td>
                          <td>{getPreviewValue(['Integration Dependencies', 'Dependencies', 'Integrations'])}</td>
                          <td>{getPreviewValue(['Application Owner', 'Owner', 'App Owner'])}</td>
                          <td>{getPreviewValue(['Technology Stack', 'Tech Stack', 'Technology'])}</td>
                          <td>{getPreviewValue(['User Count', 'Users', 'Number of Users'])}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {uploadedUpdateData.length > 10 && (
                  <p className="preview-note">Showing first 10 records. {uploadedUpdateData.length - 10} more records will be updated.</p>
                )}
              </div>
              <div className="preview-actions">
                <button 
                  className="btn btn-secondary"
                  onClick={() => { setShowUpdatePreview(false); setUploadedUpdateData([]); }}
                >
                  Cancel
                </button>
                <button 
                  className="btn btn-warning"
                  onClick={async () => {
                    setShowUpdatePreview(false);
                    await processUpdateDataDirectly(uploadedUpdateData);
                  }}
                >
                  Update {uploadedUpdateData.length} Applications
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Application Modal */}
      {viewingApplication && (
        <div className="modal-overlay" style={{ zIndex: 1100 }} onClick={() => setViewingApplication(null)}>
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white',
              borderRadius: '12px',
              padding: '24px',
              minWidth: '700px',
              maxWidth: '900px',
              maxHeight: '85vh',
              overflowY: 'auto',
              boxShadow: '0 4px 6px rgba(0,0,0,0.1), 0 8px 24px rgba(0,0,0,0.15)',
              position: 'relative',
              zIndex: 1101
            }}
          >
            <div style={{ marginBottom: '24px' }}>
              <h2 style={{
                margin: '0 0 8px 0',
                fontSize: '24px',
                fontWeight: '500',
                color: '#1a1a1a'
              }}>
                View Application
              </h2>
              <p style={{
                margin: 0,
                fontSize: '14px',
                color: '#666'
              }}>
                {viewingApplication.applicationIdCode} - {viewingApplication.name} (read-only)
              </p>
            </div>

            {/* Basic Information */}
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{
                fontSize: '16px',
                fontWeight: '600',
                color: '#333',
                marginBottom: '16px',
                paddingBottom: '8px',
                borderBottom: '2px solid #e0e0e0'
              }}>Basic Information</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#666', marginBottom: '4px' }}>Application Name</label>
                  <div style={{ fontSize: '14px', color: '#333', padding: '8px 12px', backgroundColor: '#f8f9fa', borderRadius: '6px', border: '1px solid #e0e0e0' }}>{viewingApplication.name}</div>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#666', marginBottom: '4px' }}>Application ID/Code</label>
                  <div style={{ fontSize: '14px', color: '#333', padding: '8px 12px', backgroundColor: '#f8f9fa', borderRadius: '6px', border: '1px solid #e0e0e0' }}>{viewingApplication.applicationIdCode || 'N/A'}</div>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#666', marginBottom: '4px' }}>Application Type</label>
                  <div style={{ fontSize: '14px', color: '#333', padding: '8px 12px', backgroundColor: '#f8f9fa', borderRadius: '6px', border: '1px solid #e0e0e0' }}>{viewingApplication.applicationType || 'N/A'}</div>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#666', marginBottom: '4px' }}>Date of Introduction</label>
                  <div style={{ fontSize: '14px', color: '#333', padding: '8px 12px', backgroundColor: '#f8f9fa', borderRadius: '6px', border: '1px solid #e0e0e0' }}>{viewingApplication.dateOfIntroduction || 'N/A'}</div>
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#666', marginBottom: '4px' }}>Parent Application</label>
                  <div style={{ fontSize: '14px', color: '#333', padding: '8px 12px', backgroundColor: '#f8f9fa', borderRadius: '6px', border: '1px solid #e0e0e0' }}>
                    {viewingApplication.parentApplicationCode ||
                      (viewingApplication.parentApplicationId
                        ? state.applications.find(app => app.id === viewingApplication.parentApplicationId)?.applicationIdCode
                        : 'N/A') ||
                      'N/A'}
                  </div>
                </div>
              </div>
            </div>

            {/* Technical Information */}
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{
                fontSize: '16px',
                fontWeight: '600',
                color: '#333',
                marginBottom: '16px',
                paddingBottom: '8px',
                borderBottom: '2px solid #e0e0e0'
              }}>Technical Information</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#666', marginBottom: '4px' }}>IT Hardware/Infrastructure</label>
                  <div style={{ fontSize: '14px', color: '#333', padding: '8px 12px', backgroundColor: '#f8f9fa', borderRadius: '6px', border: '1px solid #e0e0e0' }}>{viewingApplication.itHardwareInfrastructure || 'N/A'}</div>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#666', marginBottom: '4px' }}>Technology Stack</label>
                  <div style={{ fontSize: '14px', color: '#333', padding: '8px 12px', backgroundColor: '#f8f9fa', borderRadius: '6px', border: '1px solid #e0e0e0' }}>{viewingApplication.technologyStack || 'N/A'}</div>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#666', marginBottom: '4px' }}>Technical Health Status</label>
                  <div style={{ fontSize: '14px', color: '#333', padding: '8px 12px', backgroundColor: '#f8f9fa', borderRadius: '6px', border: '1px solid #e0e0e0' }}>{viewingApplication.technicalHealthStatus || 'N/A'}</div>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#666', marginBottom: '4px' }}>Integration Dependencies</label>
                  <div style={{ fontSize: '14px', color: '#333', padding: '8px 12px', backgroundColor: '#f8f9fa', borderRadius: '6px', border: '1px solid #e0e0e0' }}>{viewingApplication.integrationDependencies || 'N/A'}</div>
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{
                fontSize: '16px',
                fontWeight: '600',
                color: '#333',
                marginBottom: '16px',
                paddingBottom: '8px',
                borderBottom: '2px solid #e0e0e0'
              }}>Contact Information</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#666', marginBottom: '4px' }}>Application Owner</label>
                  <div style={{ fontSize: '14px', color: '#333', padding: '8px 12px', backgroundColor: '#f8f9fa', borderRadius: '6px', border: '1px solid #e0e0e0' }}>{viewingApplication.applicationOwner || 'N/A'}</div>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#666', marginBottom: '4px' }}>App Owner Email</label>
                  <div style={{ fontSize: '14px', color: '#333', padding: '8px 12px', backgroundColor: '#f8f9fa', borderRadius: '6px', border: '1px solid #e0e0e0' }}>{viewingApplication.appOwnerEmail || 'N/A'}</div>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#666', marginBottom: '4px' }}>Technical Owner/Custodian</label>
                  <div style={{ fontSize: '14px', color: '#333', padding: '8px 12px', backgroundColor: '#f8f9fa', borderRadius: '6px', border: '1px solid #e0e0e0' }}>{viewingApplication.technicalOwnerCustodian || 'N/A'}</div>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#666', marginBottom: '4px' }}>Technical Owner Email</label>
                  <div style={{ fontSize: '14px', color: '#333', padding: '8px 12px', backgroundColor: '#f8f9fa', borderRadius: '6px', border: '1px solid #e0e0e0' }}>{viewingApplication.toEmail || 'N/A'}</div>
                </div>
              </div>
            </div>

            {/* Business Information */}
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{
                fontSize: '16px',
                fontWeight: '600',
                color: '#333',
                marginBottom: '16px',
                paddingBottom: '8px',
                borderBottom: '2px solid #e0e0e0'
              }}>Business Information</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#666', marginBottom: '4px' }}>Business Function/Domain</label>
                  <div style={{ fontSize: '14px', color: '#333', padding: '8px 12px', backgroundColor: '#f8f9fa', borderRadius: '6px', border: '1px solid #e0e0e0' }}>{viewingApplication.businessFunctionDomain || 'N/A'}</div>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#666', marginBottom: '4px' }}>Business Criticality</label>
                  <div style={{ fontSize: '14px', color: '#333', padding: '8px 12px', backgroundColor: '#f8f9fa', borderRadius: '6px', border: '1px solid #e0e0e0' }}>{viewingApplication.businessCriticality || 'N/A'}</div>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#666', marginBottom: '4px' }}>User Count</label>
                  <div style={{ fontSize: '14px', color: '#333', padding: '8px 12px', backgroundColor: '#f8f9fa', borderRadius: '6px', border: '1px solid #e0e0e0' }}>{viewingApplication.userCount || 0}</div>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#666', marginBottom: '4px' }}>Vendor/Supplier</label>
                  <div style={{ fontSize: '14px', color: '#333', padding: '8px 12px', backgroundColor: '#f8f9fa', borderRadius: '6px', border: '1px solid #e0e0e0' }}>{viewingApplication.vendorSupplier || 'N/A'}</div>
                </div>
              </div>
            </div>

            {/* Application Description */}
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{
                fontSize: '16px',
                fontWeight: '600',
                color: '#333',
                marginBottom: '16px',
                paddingBottom: '8px',
                borderBottom: '2px solid #e0e0e0'
              }}>Application Description</h3>
              <div>
                <div style={{ fontSize: '14px', color: '#333', padding: '8px 12px', backgroundColor: '#f8f9fa', borderRadius: '6px', border: '1px solid #e0e0e0', minHeight: '60px', whiteSpace: 'pre-wrap' }}>{viewingApplication.applicationDescription || 'N/A'}</div>
              </div>
            </div>

            {/* Additional Reference */}
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{
                fontSize: '16px',
                fontWeight: '600',
                color: '#333',
                marginBottom: '16px',
                paddingBottom: '8px',
                borderBottom: '2px solid #e0e0e0'
              }}>Additional Reference</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#666', marginBottom: '4px' }}>Internal Number</label>
                  <div style={{ fontSize: '14px', color: '#333', padding: '8px 12px', backgroundColor: '#f8f9fa', borderRadius: '6px', border: '1px solid #e0e0e0' }}>{viewingApplication.internalNumber || 'N/A'}</div>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#666', marginBottom: '4px' }}>L1 - Business Functional Area</label>
                  <div style={{ fontSize: '14px', color: '#333', padding: '8px 12px', backgroundColor: '#f8f9fa', borderRadius: '6px', border: '1px solid #e0e0e0' }}>{viewingApplication.l1BusinessFunctionalArea || 'N/A'}</div>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#666', marginBottom: '4px' }}>L2 - Capability Function</label>
                  <div style={{ fontSize: '14px', color: '#333', padding: '8px 12px', backgroundColor: '#f8f9fa', borderRadius: '6px', border: '1px solid #e0e0e0' }}>{viewingApplication.l2CapabilityFunction || 'N/A'}</div>
                </div>
              </div>
            </div>

            {/* Audit Information */}
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{
                fontSize: '16px',
                fontWeight: '600',
                color: '#333',
                marginBottom: '16px',
                paddingBottom: '8px',
                borderBottom: '2px solid #e0e0e0'
              }}>Audit Information</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#666', marginBottom: '4px' }}>Created By</label>
                  <div style={{ fontSize: '14px', color: '#333', padding: '8px 12px', backgroundColor: '#f8f9fa', borderRadius: '6px', border: '1px solid #e0e0e0' }}>{viewingApplication.createdByName || 'N/A'}</div>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#666', marginBottom: '4px' }}>Created On</label>
                  <div style={{ fontSize: '14px', color: '#333', padding: '8px 12px', backgroundColor: '#f8f9fa', borderRadius: '6px', border: '1px solid #e0e0e0' }}>
                    {viewingApplication.createdAt
                      ? new Date(viewingApplication.createdAt).toLocaleString()
                      : 'N/A'}
                  </div>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#666', marginBottom: '4px' }}>Updated By</label>
                  <div style={{ fontSize: '14px', color: '#333', padding: '8px 12px', backgroundColor: '#f8f9fa', borderRadius: '6px', border: '1px solid #e0e0e0' }}>{viewingApplication.updatedByName || 'N/A'}</div>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#666', marginBottom: '4px' }}>Updated On</label>
                  <div style={{ fontSize: '14px', color: '#333', padding: '8px 12px', backgroundColor: '#f8f9fa', borderRadius: '6px', border: '1px solid #e0e0e0' }}>
                    {viewingApplication.updatedAt
                      ? new Date(viewingApplication.updatedAt).toLocaleString()
                      : 'N/A'}
                  </div>
                </div>
              </div>
            </div>

            {/* Close Button */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '24px' }}>
              <button
                type="button"
                onClick={() => setViewingApplication(null)}
                style={{
                  padding: '10px 24px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'white',
                  backgroundColor: '#1976d2',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
                onMouseOver={(e) => (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#1565c0'}
                onMouseOut={(e) => (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#1976d2'}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default ApplicationMasterData;